import { uiSurface } from "shared/ui/uiSurface"
import { ShoppingBag } from "@mui/icons-material"
import {
  Badge,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import dayjs from "dayjs"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { OrderSummary, useGetClientOrdersQuery } from "../../apis/appApi"

const stateColors: Record<string, { bg: string; fg: string }> = {
  CREATED: { bg: "#E3F2FD", fg: "#1565C0" },
  ORDER_CREATED: { bg: "#E3F2FD", fg: "#1565C0" },
  IN_PREPARATION: { bg: "#E3F2FD", fg: "#1565C0" },
  READY_FOR_HAULAGE: { bg: "#E8F5E9", fg: "#2E7D32" },
  ON_THE_WAY_TO_CUSTOMER: { bg: "#FFF3E0", fg: "#E65100" },
  ON_THE_WAY_TO_PROVIDER: { bg: "#FFF3E0", fg: "#E65100" },
  DELIVERED: { bg: "#E8F5E9", fg: "#2E7D32" },
  COMPLETED: { bg: "#E8F5E9", fg: "#2E7D32" },
  CANCELLED: { bg: "#FFEBEE", fg: "#C62828" },
  TO_PROCESS_PROVIDER: { bg: "#FFF8E1", fg: "#F57F17" },
  TO_PROCESS_PROVIDER_CLIENT: { bg: "#FFF8E1", fg: "#F57F17" },
  TO_PROCESS_PROVIDER_RECURRING: { bg: "#FFF8E1", fg: "#F57F17" },
  TO_PROCESS_PROVIDER_ERROR: { bg: "#FFEBEE", fg: "#C62828" },
}

const formatState = (state: string) =>
  state
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase())

type ClientOrdersSectionProps = {
  clientId: number
  onClose: () => void
}

export const ClientOrdersSection: FC<ClientOrdersSectionProps> = ({
  clientId,
  onClose,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: response, isLoading } = useGetClientOrdersQuery({
    clientId,
    pageSize: 5,
  })

  const orders = response?.data ?? []
  const total = response?.total ?? 0

  const handleRowClick = (order: OrderSummary) => {
    onClose()
    navigate(`/orders/${order.id}`)
  }

  return (
    <Paper
      {...uiSurface("UI-DLV2-093")}
      variant="outlined"
      sx={{ p: 2, bgcolor: "grey.50" }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ color: "text.secondary", display: "flex" }}>
          <ShoppingBag fontSize="small" />
        </Box>
        <Typography variant="subtitle2" fontWeight={600}>
          {t("clients.orders.title")}
        </Typography>
        {total > 0 && (
          <Badge
            badgeContent={total}
            color="primary"
            max={999}
            sx={{ ml: 1 }}
            data-testid="client-orders-count"
          />
        )}
      </Stack>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!isLoading && orders.length === 0 && (
        <Typography
          variant="body2"
          color="text.disabled"
          data-testid="client-orders-empty"
        >
          {t("clients.orders.noOrders")}
        </Typography>
      )}

      {!isLoading && orders.length > 0 && (
        <>
          <TableContainer>
            <Table size="small" data-testid="client-orders-table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, py: 0.5 }}>
                    {t("clients.orders.reference")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 0.5 }}>
                    {t("clients.orders.status")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 0.5 }}>
                    {t("clients.orders.date")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 0.5 }} align="right">
                    {t("clients.orders.amount")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => {
                  const sc = stateColors[order.state] ?? {
                    bg: "#F5F5F5",
                    fg: "#616161",
                  }
                  return (
                    <TableRow
                      key={order.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleRowClick(order)}
                      data-testid={`client-order-row-${order.id}`}
                    >
                      <TableCell sx={{ py: 0.75 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {order.reference}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <Chip
                          label={formatState(order.state)}
                          size="small"
                          sx={{
                            bgcolor: sc.bg,
                            color: sc.fg,
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            height: 22,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <Typography variant="body2" color="text.secondary">
                          {order.deliveryDate
                            ? dayjs(order.deliveryDate).format("MMM D, YYYY")
                            : "\u2014"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }} align="right">
                        <Typography variant="body2">
                          {order.amount != null
                            ? `$${order.amount.toFixed(2)}`
                            : "\u2014"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {total > orders.length && (
            <Typography
              variant="body2"
              color="primary"
              sx={{ mt: 1, textAlign: "center" }}
              data-testid="client-orders-view-all"
            >
              {t("clients.orders.viewAll")} ({total})
            </Typography>
          )}
        </>
      )}
    </Paper>
  )
}
